// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ApprovalRequestForm.test.tsx
import { type ParticipationTask } from "@mg/schemas/src/christo/participationTask";
import { Image as ImageIcon, X } from "@phosphor-icons/react";
import { Button, Flex, IconButton, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";

import { ChevronNavButton } from "./ChevronNavButton";

export type ApprovalRequestAction =
  | "approved"
  | "changes_requested"
  | "not_needed"
  | "note_added";

/** Visual metadata for a pending weigh-in card (from reviews panel / task). */
export type ApprovalRequestAssetVisual = {
  name: string;
  versionNumber?: number;
  thumbnailUrl?: string | null;
};

/** Parse denorm labels like "Packaging v2" into name + version. */
export function parseAssetLabel(assetLabel: string | null | undefined): {
  name: string | null;
  versionNumber?: number;
} {
  const trimmed = assetLabel?.trim();
  if (!trimmed) return { name: null };
  const match = trimmed.match(/^(.*)\s+v(\d+)\s*$/i);
  if (!match) return { name: trimmed };
  return {
    name: match[1]?.trim() || trimmed,
    versionNumber: Number(match[2]),
  };
}

function resolveCardVisual(
  task: ParticipationTask,
  visual?: ApprovalRequestAssetVisual | null,
): ApprovalRequestAssetVisual {
  if (visual?.name) {
    return {
      name: visual.name,
      versionNumber: visual.versionNumber,
      thumbnailUrl: visual.thumbnailUrl ?? null,
    };
  }
  const parsed = parseAssetLabel(task.assetLabel);
  return {
    name: parsed.name ?? "Review asset",
    versionNumber: parsed.versionNumber,
    thumbnailUrl: null,
  };
}

/**
 * In-feed approval cards: one open participation task per version.
 * Project chat (default) paginates multiple cards; compact surfaces stack.
 * Presentational — parent owns mutations, busy state, and asset visuals.
 */
export type ApprovalCardHighlightTarget = {
  fileId: string;
  versionId: string;
  ticketId: string | null;
};

export type ApprovalRespondInput = {
  task: ParticipationTask;
  action: ApprovalRequestAction;
  noteBody?: string;
};

type StickyPostDenyCard = {
  task: ParticipationTask;
  visual: ApprovalRequestAssetVisual;
};

function ApprovalRequestCard({
  task,
  busy,
  visual,
  compact,
  postDenyNote,
  onRespond,
  onDismissPostDenyNote,
  onCardHighlight,
}: {
  task: ParticipationTask;
  busy: boolean;
  visual: ApprovalRequestAssetVisual;
  compact: boolean;
  /** True after deny was submitted — optional note for the already-denied decision. */
  postDenyNote: boolean;
  onRespond: (input: ApprovalRespondInput) => void;
  onDismissPostDenyNote: () => void;
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
}) {
  const [noteDraft, setNoteDraft] = useState("");

  const kindLabel = task.kind === "invited" ? "Invited" : "Suggested";
  const title = visual.name;
  const versionLabel =
    visual.versionNumber != null ? `v${visual.versionNumber}` : null;
  const highlightTarget: ApprovalCardHighlightTarget = {
    fileId: task.fileId,
    versionId: task.versionId,
    ticketId: task.ticketId,
  };

  const trimmedNote = noteDraft.trim();
  const canAddNote = trimmedNote.length > 0 && !busy;

  const handleDeny = () => {
    onRespond({ task, action: "changes_requested" });
  };

  const handleAddNote = () => {
    if (!canAddNote) return;
    onRespond({
      task,
      action: "note_added",
      noteBody: trimmedNote,
    });
    setNoteDraft("");
  };

  const handleSkipNote = () => {
    setNoteDraft("");
    onDismissPostDenyNote();
  };

  return (
    <section
      className={
        compact
          ? "relative overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-3 shadow-sm outline-none focus-within:border-puntt-accent-7 focus-within:ring-2 focus-within:ring-puntt-accent-6/30"
          : "relative overflow-hidden rounded-2xl border border-puntt-neutral-gray-5 bg-base-white p-4 shadow-sm outline-none focus-within:border-puntt-accent-7 focus-within:ring-2 focus-within:ring-puntt-accent-6/30"
      }
      data-testid={`approval-request-card-${task.versionId}`}
      aria-label={
        postDenyNote
          ? `Add a note to your denial of ${title}`
          : `Weigh in on ${title}`
      }
      onMouseEnter={() => onCardHighlight?.(highlightTarget)}
      onMouseLeave={() => onCardHighlight?.(null)}
      onFocusCapture={() => onCardHighlight?.(highlightTarget)}
      onBlurCapture={(e) => {
        const next = e.relatedTarget as Node | null;
        if (!next || !e.currentTarget.contains(next)) {
          onCardHighlight?.(null);
        }
      }}
    >
      {!postDenyNote ? (
        <IconButton
          type="button"
          size="1"
          variant="ghost"
          color="gray"
          className="absolute right-2 top-2 z-10"
          data-testid={`approval-request-not-needed-${task.versionId}`}
          aria-label="Not needed from me"
          disabled={busy}
          onClick={() => onRespond({ task, action: "not_needed" })}
        >
          <X size={14} aria-hidden="true" />
        </IconButton>
      ) : null}

      <div
        className={`mb-1 flex flex-wrap items-center gap-2 ${postDenyNote ? "" : "pr-7"}`}
      >
        <p className="text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
          {postDenyNote ? "Denied — optional note" : "Needs your decision"}
        </p>
        <span
          className="rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-puntt-neutral-gray-11"
          data-testid={`approval-request-kind-${task.versionId}`}
        >
          {kindLabel}
        </span>
        {task.suggestedRole ? (
          <span
            className="text-[11px] text-puntt-neutral-gray-10"
            data-testid={`approval-request-role-${task.versionId}`}
          >
            {task.suggestedRole}
          </span>
        ) : null}
      </div>

      <div
        className={
          compact
            ? "mt-2 flex items-stretch gap-2.5"
            : "mt-3 flex items-stretch gap-3"
        }
        data-testid={`approval-request-asset-${task.versionId}`}
      >
        <span
          aria-hidden="true"
          data-testid={`approval-request-thumbnail-${task.versionId}`}
          className={
            compact
              ? "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
              : "flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11"
          }
        >
          {visual.thumbnailUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text -- decorative; card has aria-label
            <img
              src={visual.thumbnailUrl}
              alt=""
              aria-hidden="true"
              className="size-full object-cover"
            />
          ) : (
            <ImageIcon size={22} weight="fill" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h3
            className={
              compact
                ? "truncate text-[14px] font-semibold text-puntt-neutral-gray-12"
                : "truncate text-[15px] font-semibold text-puntt-neutral-gray-12"
            }
            data-testid={`approval-request-title-${task.versionId}`}
          >
            {title}
          </h3>
          {versionLabel ? (
            <p
              className="mt-0.5 text-[12px] text-puntt-neutral-gray-10"
              data-testid={`approval-request-version-${task.versionId}`}
            >
              {versionLabel}
            </p>
          ) : null}
          {task.reasoning?.trim() ? (
            <p
              className="mt-2 text-[12px] leading-relaxed text-puntt-neutral-gray-11"
              data-testid={`approval-request-reasoning-${task.versionId}`}
            >
              {task.reasoning.trim()}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {!postDenyNote ? (
          <div
            className="flex items-center gap-2"
            role="group"
            aria-label={`Choose a response for ${title}`}
          >
            <Button
              type="button"
              size="1"
              data-testid={`approval-request-approve-${task.versionId}`}
              aria-label="Approve"
              disabled={busy}
              onClick={() => onRespond({ task, action: "approved" })}
            >
              {busy ? "Submitting…" : "Approve"}
            </Button>
            <Button
              type="button"
              size="1"
              variant="outline"
              color="gray"
              data-testid={`approval-request-deny-${task.versionId}`}
              aria-label="Deny"
              disabled={busy}
              onClick={handleDeny}
            >
              Deny
            </Button>
          </div>
        ) : (
          <div
            className="approval-deny-slide-in flex flex-col gap-2"
            role="group"
            aria-label={`Add an optional note to your denial of ${title}`}
            data-testid={`approval-request-deny-step-${task.versionId}`}
          >
            <textarea
              className="w-full resize-none rounded-md border border-puntt-neutral-gray-6 bg-base-white px-2.5 py-2 text-[13px] text-puntt-neutral-gray-12 placeholder:text-puntt-neutral-gray-9 focus:border-puntt-accent-7 focus:outline-none focus:ring-2 focus:ring-puntt-accent-6/30 disabled:cursor-not-allowed disabled:opacity-60"
              rows={2}
              placeholder="Optional note (why you requested changes)"
              value={noteDraft}
              disabled={busy}
              data-testid={`approval-request-deny-note-${task.versionId}`}
              aria-label="Optional note for your denial"
              onChange={(event) => setNoteDraft(event.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="1"
                data-testid={`approval-request-deny-confirm-${task.versionId}`}
                aria-label="Add note to denial"
                disabled={!canAddNote}
                onClick={handleAddNote}
              >
                {busy ? "Adding…" : "Add note"}
              </Button>
              <Button
                type="button"
                size="1"
                variant="outline"
                color="gray"
                data-testid={`approval-request-deny-cancel-${task.versionId}`}
                aria-label="Skip note"
                disabled={busy}
                onClick={handleSkipNote}
              >
                Skip
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function ApprovalRequestForm({
  tasks,
  busyVersionId = null,
  assetsByVersionId = {},
  onRespond,
  onCardHighlight,
  onPostDenySettled,
  variant = "default",
}: {
  tasks: ParticipationTask[];
  busyVersionId?: string | null;
  /** Thumbnail / display name keyed by versionId (from project reviews). */
  assetsByVersionId?: Record<string, ApprovalRequestAssetVisual>;
  onRespond: (input: ApprovalRespondInput) => void;
  /** Hover/focus on a card — parent highlights the matching Reviews sidebar row. */
  onCardHighlight?: (target: ApprovalCardHighlightTarget | null) => void;
  /** Fired when the post-deny note step is skipped or a note is submitted. */
  onPostDenySettled?: (versionId: string) => void;
  /** Denser layout for home popover and other compact surfaces. */
  variant?: "default" | "compact";
}) {
  const compact = variant === "compact";
  const [stickyPostDeny, setStickyPostDeny] = useState<
    Record<string, StickyPostDenyCard>
  >({});
  /** Versions whose post-deny note step finished — hide even if still in open tasks. */
  const [finishedDenyVersions, setFinishedDenyVersions] = useState<
    Record<string, true>
  >({});

  const openVersionIds = useMemo(
    () => new Set(tasks.map((task) => task.versionId)),
    [tasks],
  );

  const cards = useMemo(() => {
    const openCards = tasks
      .filter((task) => finishedDenyVersions[task.versionId] == null)
      .map((task) => {
        const visual = resolveCardVisual(
          task,
          assetsByVersionId[task.versionId],
        );
        return {
          task,
          visual,
          postDenyNote: stickyPostDeny[task.versionId] != null,
        };
      });
    const stickyOnly = Object.values(stickyPostDeny)
      .filter((entry) => !openVersionIds.has(entry.task.versionId))
      .map((entry) => ({
        task: entry.task,
        visual: entry.visual,
        postDenyNote: true,
      }));
    return [...openCards, ...stickyOnly];
  }, [
    tasks,
    assetsByVersionId,
    stickyPostDeny,
    openVersionIds,
    finishedDenyVersions,
  ]);

  const clearPostDeny = (versionId: string) => {
    setStickyPostDeny((prev) => {
      if (prev[versionId] == null) return prev;
      const next = { ...prev };
      delete next[versionId];
      return next;
    });
  };

  const dismissPostDeny = (versionId: string) => {
    clearPostDeny(versionId);
    setFinishedDenyVersions((prev) =>
      prev[versionId] ? prev : { ...prev, [versionId]: true },
    );
    onPostDenySettled?.(versionId);
  };

  const handleRespond = (input: ApprovalRespondInput) => {
    if (input.action === "changes_requested") {
      const visual = resolveCardVisual(
        input.task,
        assetsByVersionId[input.task.versionId],
      );
      setStickyPostDeny((prev) => ({
        ...prev,
        [input.task.versionId]: { task: input.task, visual },
      }));
    } else if (input.action === "note_added") {
      dismissPostDeny(input.task.versionId);
    } else if (input.action === "approved" || input.action === "not_needed") {
      clearPostDeny(input.task.versionId);
    }
    onRespond(input);
  };

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (cards.length === 0) return;
    setActiveIndex((prev) => {
      const prevVersionId = cards[prev]?.task?.versionId;
      if (prevVersionId != null) {
        const sameVersionIdx = cards.findIndex(
          (card) => card.task.versionId === prevVersionId,
        );
        if (sameVersionIdx >= 0) return sameVersionIdx;
      }
      return Math.min(prev, cards.length - 1);
    });
  }, [cards]);

  if (cards.length === 0) return null;

  const renderCard = ({
    task,
    visual,
    postDenyNote,
  }: (typeof cards)[number]) => {
    const busy = busyVersionId === task.versionId;

    return (
      <ApprovalRequestCard
        key={task.versionId}
        task={task}
        busy={busy}
        visual={visual}
        compact={compact}
        postDenyNote={postDenyNote}
        onRespond={handleRespond}
        onDismissPostDenyNote={() => dismissPostDeny(task.versionId)}
        onCardHighlight={onCardHighlight}
      />
    );
  };

  if (compact) {
    return (
      <div
        className="flex flex-col gap-2"
        data-testid="approval-request-form"
        aria-label="Pending approval requests"
      >
        {cards.map(renderCard)}
      </div>
    );
  }

  const clampedIndex = Math.min(activeIndex, cards.length - 1);
  const activeCard = cards[clampedIndex];
  const showNav = cards.length > 1;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="approval-request-form"
      aria-label="Pending approval requests"
    >
      {showNav ? (
        <Flex
          align="center"
          justify="center"
          gap="2"
          className="shrink-0 rounded-xl border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-3 py-1.5"
          data-testid="approval-request-nav"
          aria-label="Browse pending approval requests"
        >
          <ChevronNavButton
            direction="left"
            onClick={() => setActiveIndex((idx) => Math.max(0, idx - 1))}
            disabled={clampedIndex === 0}
            aria-label="Previous approval request"
            data-testid="approval-request-nav-prev"
          />
          <Text
            size="1"
            className="text-puntt-neutral-gray-11"
            data-testid="approval-request-nav-label"
          >
            {clampedIndex + 1} of {cards.length}
          </Text>
          <ChevronNavButton
            direction="right"
            onClick={() =>
              setActiveIndex((idx) => Math.min(cards.length - 1, idx + 1))
            }
            disabled={clampedIndex === cards.length - 1}
            aria-label="Next approval request"
            data-testid="approval-request-nav-next"
          />
        </Flex>
      ) : null}
      {renderCard(activeCard)}
    </div>
  );
}
